"use client";

import { useAuth, useSignIn, useSignUp, useUser } from "@clerk/nextjs";
import { taskError } from "@/lib/toast";
import Link from "next/link";
import NextImage from "next/image";
import { Button } from "@/components/ui/button";
import GoogleIcon from "@/assets/icons/google-color-svgrepo-com.svg";
import { EmailAuthFlow } from "@/components/auth/email-auth-flow";
import { useEffect, useLayoutEffect, useCallback, useState, startTransition, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { trackEvent, trackEventWithDelay } from "@/lib/utils/analytics";
import Logger from "@/lib/utils/logger";
import { getAffinityCodeClient, clearAffinityCode } from "@/lib/utils/affinityCookies";
import { getToltSignupUnsafeMetadata } from "@/lib/tolt/attribution";
import { landingEventProperties, rememberSignupChoice } from "@/lib/analytics/landingCookie";
import { getLandingSignupUnsafeMetadata } from "@/lib/analytics/landingSignup";
import { useIsMobile } from "@/hooks/useIsMobile";
import { referrerDisplayName, useReferrerPreview } from "@/hooks/tolt/useReferrer";
import { getAffinityByCode } from "@/hooks/admin/affinities";
import { useQuery } from "@tanstack/react-query";
import { SolidCard } from "@/components/shared/SolidCard";
import { SmartAssistantAvatar } from "@/components/smartassistant/SmartAssistantAvatar";
import { useAssistantIdentity } from "@/hooks/assistant/useAssistantIdentity";
import { Check, Loader2 } from "lucide-react";
type AuthStep = "choose" | "email" | "otp" | "google-redirect";
export function LandingContent({
  view: _view = "email",
  initialAffinityCode = null,
  clearAffinityCookie = false,
  deviceLinkReturn = null,
  initialRefCode = null
}: {
  view?: "email" | "otp";
  initialAffinityCode?: string | null;
  /** If true, clears any existing affinity cookie (used when accessing /landing directly) */
  clearAffinityCookie?: boolean;
  /** After sign-in, navigate here (e.g. desktop device-link flow). */
  deviceLinkReturn?: string | null;
  /** Tolt affiliate code from ?ref= — shows the "Referred by" banner. */
  initialRefCode?: string | null;
}) {
  const {
    isLoaded: isAuthLoaded
  } = useAuth();
  const {
    signIn
  } = useSignIn();
  const {
    signUp
  } = useSignUp();
  const isSignInLoaded = isAuthLoaded && signIn != null;
  const isSignUpLoaded = isAuthLoaded && signUp != null;
  const {
    isSignedIn
  } = useUser();
  const t = useTranslations("Authentication");
  const {
    name: assistantName,
    icon: assistantIcon
  } = useAssistantIdentity();
  /** Matches OTP completion destination (device link vs onboarding check). */
  const postOtpRedirectPath = deviceLinkReturn?.startsWith("/") ? deviceLinkReturn : "/onboarding/check";
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auth flow state — mirrors onboarding: completedSteps grow above a single bottomRef div.
  const [authMode, setAuthMode] = useState<"sign-in" | "sign-up">("sign-in");
  const [currentStep, setCurrentStep] = useState<AuthStep>("choose");
  const [completedSteps, setCompletedSteps] = useState<Array<{
    step: AuthStep;
    content: React.ReactNode;
    response: React.ReactNode;
  }>>([]);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [isGoogleRedirecting, setIsGoogleRedirecting] = useState(false);
  const [_chosenMethod, setChosenMethod] = useState<"google" | "email" | null>(null);
  const [scrollRequestId, setScrollRequestId] = useState(0);

  // Affinity code state
  const [affinityCode, setAffinityCode] = useState<string | null>(initialAffinityCode);

  // Clear affinity cookie if requested (when accessing /landing directly)
  useEffect(() => {
    if (clearAffinityCookie && typeof window !== "undefined") {
      const existingCode = getAffinityCodeClient();
      if (existingCode) {
        clearAffinityCode();
        Logger.info("Affinity cookie cleared on landing page", {
          previousCode: existingCode
        });
      }
    }
  }, [clearAffinityCookie]);

  // Get affinity code from client-side cookie if not provided server-side
  useEffect(() => {
    if (!affinityCode && !clearAffinityCookie) {
      startTransition(() => {
        const clientCode = getAffinityCodeClient();
        if (clientCode) {
          setAffinityCode(clientCode);
        }
      });
    }
  }, [affinityCode, clearAffinityCookie]);

  // Tolt affiliate referral (?ref=) — display-safe partner name or null
  const {
    data: referrerData
  } = useReferrerPreview(initialRefCode);
  const referrer = referrerData?.referrer ?? null;

  // Check for affinity code and fetch affinity data
  const {
    data: affinity,
    isLoading: isLoadingAffinity
  } = useQuery({
    queryKey: ["affinity", affinityCode],
    queryFn: async () => {
      if (!affinityCode) return null;
      try {
        const result = await getAffinityByCode(affinityCode);
        Logger.info("Fetched affinity data", {
          code: affinityCode,
          hasData: !!result
        });
        return result;
      } catch (error) {
        Logger.error("Failed to fetch affinity", error, {
          code: affinityCode
        });
        return null;
      }
    },
    enabled: !!affinityCode,
    staleTime: 5 * 60 * 1000
  });

  // Debug logging
  useEffect(() => {
    if (affinityCode) {
      Logger.info("Affinity code in LandingContent", {
        code: affinityCode,
        hasAffinity: !!affinity,
        isLoading: isLoadingAffinity,
        affinityActive: affinity?.isActive
      });
    }
  }, [affinityCode, affinity, isLoadingAffinity]);

  // Smooth-scroll to bottomRef. Stored in a ref so unrelated re-renders can't cancel it.
  const SCROLL_DURATION_MS = 1100;
  const scrollRafRef = useRef<number | null>(null);
  const startScrollToBottom = useCallback(() => {
    // Cancel any in-flight animation
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }

    // Wait one frame so React has committed the DOM update
    scrollRafRef.current = requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      const targetEl = bottomRef.current;
      if (!container || !targetEl) return;
      const startTop = container.scrollTop;
      const targetTop = startTop + (targetEl.getBoundingClientRect().top - container.getBoundingClientRect().top);
      const distance = targetTop - startTop;
      if (Math.abs(distance) < 2) return;
      const startTime = performance.now();
      const easeInOutCubic = (t_0: number) => t_0 < 0.5 ? 4 * t_0 * t_0 * t_0 : 1 - (-2 * t_0 + 2) ** 3 / 2;
      const tick = (now: number) => {
        if (!scrollContainerRef.current) return;
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / SCROLL_DURATION_MS, 1);
        const eased = easeInOutCubic(progress);
        scrollContainerRef.current.scrollTop = startTop + distance * eased;
        if (progress < 1) {
          scrollRafRef.current = requestAnimationFrame(tick);
        } else {
          scrollRafRef.current = null;
        }
      };
      scrollRafRef.current = requestAnimationFrame(tick);
    });
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  // Run scroll only after React commits the new step layout.
  useLayoutEffect(() => {
    if (scrollRequestId === 0) return;
    startScrollToBottom();
  }, [scrollRequestId, startScrollToBottom]);

  // Build completed "choose" content to match what the user saw (same structure as live step)
  const completedChooseContent = <div className="space-y-4">
      <h1 className="font-bold text-primary-foreground text-lg">{t("landing.title")}</h1>
      <p className="text-primary-foreground text-sm">{t("landing.subtitle")}</p>
      <p className="text-primary-foreground text-sm">{t("landing.assistantIntro")}</p>
      {affinity && affinity.isActive && <div className="flex items-center gap-2 mt-4 pt-4 border-white/10 border-t">
          <p className="text-primary-foreground/50 text-sm">{t("landing.broughtToYouBy")}</p>
          {affinity.imageUrl && <NextImage src={affinity.imageUrl} alt={affinity.name || affinity.partnerName} width={24} height={24} className="rounded" />}
          <p className="font-medium text-primary-foreground text-sm">{affinity.partnerName}</p>
        </div>}
      <p className="pt-4 text-primary-foreground/50 text-xs">
        {t("landing.legalIntro")}{" "}
        <Link href="https://www.thesukha.co/terms" className="hover:text-white/70 underline">
          {t("landing.termsOfUse")}
        </Link>{" "}
        {t("landing.and")}{" "}
        <Link href="https://www.thesukha.co/privacy" className="hover:text-white/70 underline">
          {t("landing.privacyPolicy")}
        </Link>
        {t("landing.legalClose")}
      </p>
    </div>;

  // Handle choosing email method — push "choose" into completed, advance to email
  const handleChooseEmail = () => {
    setChosenMethod("email");
    rememberSignupChoice("email");
    // The email option on the sign-up screen, before an address is typed —
    // so the funnel can tell "picked email and left" from "typed an email".
    trackEvent("212-uv_clicks_email_option_from_signinup_modal", signupEventProperties());
    setCompletedSteps(prev => [...prev, {
      step: "choose" as AuthStep,
      content: completedChooseContent,
      response: <span className="text-primary-foreground text-sm">{t("landing.continueWithEmail")}</span>
    }]);
    setCurrentStep("email");
    setScrollRequestId(prev_0 => prev_0 + 1);
  };

  // Handle email submission — push "email" into completed, advance to OTP
  const handleEmailSubmit = (email: string) => {
    setSubmittedEmail(email);
    setCompletedSteps(prev_1 => [...prev_1, {
      step: "email" as AuthStep,
      content: <p className="text-primary-foreground text-sm">
            {t("landing.emailQuestion")}
          </p>,
      response: <span className="text-primary-foreground text-sm">{email}</span>
    }]);
    setCurrentStep("otp");
    setScrollRequestId(prev_2 => prev_2 + 1);
  };

  // Handle going back to email step from OTP
  const handleBackToEmail = () => {
    setSubmittedEmail(null);
    setCompletedSteps(prev_3 => prev_3.slice(0, -1));
    setCurrentStep("email");
  };

  // Redirect already signed-in users (same target as post-email OTP; hard navigation for prod middleware)
  useEffect(() => {
    if (!isSignedIn) return;
    window.location.assign(postOtpRedirectPath);
  }, [isSignedIn, postOtpRedirectPath]);

  // The app-landing stage of the onboarding funnel. Mobile is its own event
  // so the admin page can draw the mobile redirect as a labelled branch
  // rather than a drop, and the source page rides along so anonymous stages
  // can be grouped by the marketing page that sent them.
  const {
    isMobileDevice,
    hasResolved
  } = useIsMobile();
  const hasTrackedLandingRef = useRef(false);
  const signupEventProperties = () => ({
    ...landingEventProperties(),
    device: isMobileDevice ? "mobile" : "desktop"
  });
  useEffect(() => {
    if (!hasResolved || hasTrackedLandingRef.current) return;
    hasTrackedLandingRef.current = true;
    trackEvent(isMobileDevice ? "201-uv_loads_app_signinup_modal_from_mobile" : "200-uv_loads_app_signinup_modal_from_desktop", {
      ...landingEventProperties(),
      device: isMobileDevice ? "mobile" : "desktop"
    });
  }, [hasResolved, isMobileDevice]);
  const handleGoogleSignUp = async () => {
    const oauthClient = authMode === "sign-in" ? isSignInLoaded && signIn != null ? {
      mode: "sign-in" as const,
      client: signIn
    } : null : isSignUpLoaded && signUp != null ? {
      mode: "sign-up" as const,
      client: signUp
    } : null;
    if (oauthClient == null) return;
    try {
      setChosenMethod("google");
      // A new Google account is created from a sign-in attempt, which carries
      // no metadata; the cookie is what reaches the server in that case.
      rememberSignupChoice("google");
      setIsGoogleRedirecting(true);
      setCompletedSteps(prev_4 => [...prev_4, {
        step: "choose" as AuthStep,
        content: completedChooseContent,
        response: <div className="flex items-center gap-2">
              <GoogleIcon className="w-4 h-4" />
              <span className="text-primary-foreground text-sm">{t("landing.continueWithGoogle")}</span>
            </div>
      }]);
      setCurrentStep("google-redirect");
      setScrollRequestId(prev_5 => prev_5 + 1);
      const ssoBase = {
        strategy: "oauth_google" as const,
        redirectUrl: postOtpRedirectPath,
        redirectCallbackUrl: "/sso-google-callback"
      };
      if (oauthClient.mode === "sign-in") {
        const {
          error: error_0
        } = await oauthClient.client.sso(ssoBase);
        if (error_0) {
          throw error_0;
        }
      } else {
        const {
          error: error_1
        } = await oauthClient.client.sso({
          ...ssoBase,
          legalAccepted: true,
          unsafeMetadata: {
            ...getToltSignupUnsafeMetadata(),
            ...getLandingSignupUnsafeMetadata("google")
          }
        });
        if (error_1) {
          throw error_1;
        }
      }
    } catch (err) {
      Logger.error("Error during Google sign in:", err);
      taskError(t("landing.errors.googleSignIn"));
      setIsGoogleRedirecting(false);
      setChosenMethod(null);
      setCurrentStep("choose");
    }
  };
  return <SolidCard sukhaCardClassName="!justify-start h-[600px] !max-w-lg">
      <div className="flex flex-col w-full h-full min-h-0">
        {/* Header with the assistant's icon and title */}
        <div className="flex items-center gap-3 px-6 py-4 shrink-0">
          <SmartAssistantAvatar name={assistantName} icon={assistantIcon} size="lg" />
          <div className="flex flex-col">
            <span className="font-medium text-primary-foreground text-base">
              {t("landing.headerTitle")}
            </span>
            {referrer && <span className="text-secondary-300 text-xs">
                {t("landing.referredBy")} {referrerDisplayName(referrer)}
              </span>}
          </div>
        </div>

        {/* Darker inner content area — overflow-anchor: none so the browser doesn't adjust scroll when we add content above */}
        <div ref={scrollContainerRef} className="flex flex-col flex-1 bg-black/30 mx-4 mb-4 rounded-xl min-h-0 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-primary/50" style={{
        overflowAnchor: "none"
      }}>
          {/* Completed steps — read-only blocks pushed above the current step (same pattern as onboarding) */}
          {completedSteps.map((completed, index) => <div key={`completed-${completed.step}-${index}`} className="flex flex-col min-h-full px-6 py-6 shrink-0">
              <div className="flex-1 flex flex-col">
                {completed.content}
              </div>
              <div className="flex justify-end pt-4">
                <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 opacity-60">
                  <Check className="size-4 text-green-500 shrink-0" />
                  {completed.response}
                </div>
              </div>
            </div>)}

          {/* Current step — always rendered, never conditionally mounted (like onboarding's bottomRef div) */}
          <div ref={bottomRef} className="flex flex-col min-h-full px-6 py-6 shrink-0">
            {currentStep === "choose" ? <>
                {/* Welcome content */}
                <div className="flex-1">
                  <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3
              }} className="space-y-4">
                    <h1 className="font-bold text-primary-foreground text-lg">
                      {t("landing.title")}
                    </h1>
                    <p className="text-primary-foreground text-sm">
                      {t("landing.subtitle")}
                    </p>
                    <p className="text-primary-foreground text-sm">
                      {t("landing.assistantIntro")}
                    </p>

                    {affinity && affinity.isActive && <div className="flex items-center gap-2 mt-4 pt-4 border-white/10 border-t">
                        <p className="text-primary-foreground/50 text-sm">
                          {t("landing.broughtToYouBy")}
                        </p>
                        {affinity.imageUrl && <NextImage src={affinity.imageUrl} alt={affinity.name || affinity.partnerName} width={24} height={24} className="rounded" />}
                        <p className="font-medium text-primary-foreground text-sm">
                          {affinity.partnerName}
                        </p>
                      </div>}

                    <p className="pt-4 text-primary-foreground/50 text-xs">
                      {t("landing.legalIntro")}{" "}
                      <Link href="https://www.thesukha.co/terms" className="hover:text-white/70 underline">
                        {t("landing.termsOfUse")}
                      </Link>{" "}
                      {t("landing.and")}{" "}
                      <Link href="https://www.thesukha.co/privacy" className="hover:text-white/70 underline">
                        {t("landing.privacyPolicy")}
                      </Link>
                      {t("landing.legalClose")}
                    </p>
                  </motion.div>
                </div>

                {/* Buttons at bottom */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: 0.2
            }} className="pt-4">
                  <div className="flex flex-col items-end space-y-3">
                    <Button onClick={async () => {
                  await trackEventWithDelay("211-uv-clicks_google_sso_from_signinup_modal", signupEventProperties());
                  handleGoogleSignUp();
                }} disabled={(authMode === "sign-in" ? !isSignInLoaded : !isSignUpLoaded) || isGoogleRedirecting} className="gap-2 bg-secondary-400 hover:bg-secondary-500 px-6 font-medium text-primary-foreground rounded-full">
                      <GoogleIcon className="w-5 h-5" />
                      {t("landing.continueWithGoogle")}
                    </Button>
                    <Button variant="outline" onClick={handleChooseEmail} className="gap-2 bg-transparent hover:bg-white/10 px-6 border-white/30 font-medium text-primary-foreground rounded-full">
                      {t("landing.continueWithEmail")}
                    </Button>
                  </div>
                </motion.div>
              </> : currentStep === "google-redirect" ? <div className="flex flex-col justify-center items-center flex-1">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} className="flex flex-col items-center gap-4">
                  <Loader2 className="size-8 text-secondary-400 animate-spin" />
                  <p className="text-primary-foreground text-base">{t("landing.signingInWithGoogle")}</p>
                </motion.div>
              </div> : currentStep === "email" ? <motion.div key="email-step" initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="flex flex-col h-full">
                <div className="flex-1">
                  <p className="text-primary-foreground text-sm">
                    {t("landing.emailQuestion")}
                  </p>
                </div>
                <div className="flex justify-end pt-4">
                  <div className="w-full max-w-xs">
                    <EmailAuthFlow onEmailSubmit={handleEmailSubmit} postOtpRedirectPath={postOtpRedirectPath} mode={authMode} onModeChange={setAuthMode} conversational={true} initialView="email" />
                  </div>
                </div>
              </motion.div> : <motion.div key="otp-step" initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="flex flex-col h-full">
                <div className="flex-1 space-y-4">
                  <p className="text-primary-foreground text-sm">
                    {t("landing.otpSentTitle")}
                  </p>
                  <p className="text-primary-foreground font-medium text-sm text-center">
                    {submittedEmail}
                  </p>
                  <p className="text-primary-foreground text-sm">
                    {t("landing.otpCheckEmail")}
                  </p>
                </div>
                <div className="flex justify-end pt-4">
                  <div className="w-full max-w-xs">
                    <EmailAuthFlow onEmailSubmit={handleEmailSubmit} postOtpRedirectPath={postOtpRedirectPath} mode={authMode} onModeChange={setAuthMode} conversational={true} initialView="otp" prefilledEmail={submittedEmail || ""} onBackToEmail={handleBackToEmail} hideReadOnlyEmail={true} />
                  </div>
                </div>
              </motion.div>}
          </div>
        </div>
      </div>
    </SolidCard>;
}